import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { EditorContent, useEditor, Extension } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import OrderedList from "@tiptap/extension-ordered-list";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { Color } from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "../../../../../components/MDBox";
import MDTypography from "../../../../../components/MDTypography";
import MDInput from "../../../../../components/MDInput";
import MDButton from "../../../../../components/MDButton";

// Material Dashboard 2 React context
import { useMaterialUIController } from "../../../../../context";

// Services
import sectionService from "../../../../../services/sectionService";
import institutionService from "../../../../../services/institutionService";

// Utils
import { getImageUrl } from "../../../../../utils/axiosConfig";

// Styles
import "./editorStyles.css";

const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return {
      types: ['textStyle'],
    }
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize.replace(/['"]+/g, ''),
            renderHTML: attributes => {
              if (!attributes.fontSize) {
                return {}
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              }
            },
          },
        },
      },
    ]
  },
  addCommands() {
    return {
      setFontSize: fontSize => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize })
          .run()
      },
      unsetFontSize: () => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize: null })
          .removeEmptyTextStyle()
          .run()
      },
    }
  },
});

const CustomOrderedList = OrderedList.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      type: {
        default: '1',
        parseHTML: element => element.getAttribute('type'),
        renderHTML: attributes => {
          return {
            type: attributes.type,
          }
        },
      },
    }
  },
});

const MenuBar = ({ editor }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  if (!editor) {
    return null;
  }

  const sizes = [
    { label: "Pequeño", value: "12px" },
    { label: "Regular", value: "16px" },
    { label: "Grande", value: "20px" },
    { label: "Muy Grande", value: "24px" },
    { label: "Gigante", value: "32px" },
  ];

  return (
    <MDBox display="flex" gap={1} p={1} flexWrap="wrap" bgColor={darkMode ? "transparent" : "#f0f2f5"} borderRadius="lg" mb={2} sx={{ border: darkMode ? "1px solid #444" : "none" }}>
      <MDButton
        size="small"
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        color={editor.isActive("bold") ? "dark" : "white"}
      >
        <Icon>format_bold</Icon>
      </MDButton>
      <MDButton
        size="small"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        color={editor.isActive("italic") ? "dark" : "white"}
      >
        <Icon>format_italic</Icon>
      </MDButton>
      <MDButton
        size="small"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        color={editor.isActive("underline") ? "dark" : "white"}
        sx={{ minWidth: '40px' }}
      >
        <Icon>format_underlined</Icon>
      </MDButton>

      <MDBox display="flex" alignItems="center" bgcolor={darkMode ? "transparent" : "white"} borderRadius="md" px={1} border={`1px solid ${darkMode ? "#444" : "#ddd"}`}>
        <input
          type="color"
          onInput={e => editor.chain().focus().setColor(e.target.value).run()}
          value={editor.getAttributes('textStyle').color || '#000000'}
          style={{ width: '25px', height: '25px', border: 'none', cursor: 'pointer', background: 'transparent' }}
          title="Color de texto"
        />
        <Icon sx={{ fontSize: '18px', ml: 0.5 }}>palette</Icon>
      </MDBox>

      <FormControl size="small" sx={{ minWidth: 120 }}>
        <Select
          displayEmpty
          value={editor.getAttributes('textStyle').fontSize || "16px"}
          onChange={(e) => editor.chain().focus().setFontSize(e.target.value).run()}
          sx={{ height: "34px", fontSize: "14px", bgcolor: darkMode ? "transparent" : 'white' }}
        >
          {sizes.map(s => (
            <MenuItem key={s.value} value={s.value} sx={{ fontSize: s.value }}>{s.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <MDButton
        size="small"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        color={editor.isActive("heading", { level: 1 }) ? "dark" : "white"}
      >
        H1
      </MDButton>
      <MDButton
        size="small"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        color={editor.isActive("heading", { level: 2 }) ? "dark" : "white"}
      >
        H2
      </MDButton>
      <MDButton
        size="small"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        color={editor.isActive("bulletList") ? "dark" : "white"}
      >
        <Icon>format_list_bulleted</Icon>
      </MDButton>

      <MDBox display="flex" alignItems="center" gap={0.5}>
        <MDButton
          title="Lista numerada (1, 2, 3)"
          size="small"
          onClick={() => {
            if (editor.isActive("orderedList") && editor.getAttributes("orderedList").type === "1") {
              editor.chain().focus().toggleOrderedList().run();
            } else {
              editor.chain().focus().toggleOrderedList().updateAttributes("orderedList", { type: "1" }).run();
            }
          }}
          color={editor.isActive("orderedList") && editor.getAttributes("orderedList").type === "1" ? "dark" : "white"}
        >
          <Icon>format_list_numbered</Icon>
        </MDButton>

        <MDButton
          title="Lista de letras (a, b, c)"
          size="small"
          onClick={() => {
            if (editor.isActive("orderedList") && editor.getAttributes("orderedList").type === "a") {
              editor.chain().focus().toggleOrderedList().run();
            } else {
              editor.chain().focus().toggleOrderedList().updateAttributes("orderedList", { type: "a" }).run();
            }
          }}
          color={editor.isActive("orderedList") && editor.getAttributes("orderedList").type === "a" ? "dark" : "white"}
        >
          <MDTypography variant="button" fontWeight="bold" color="inherit">abc</MDTypography>
        </MDButton>
      </MDBox>

      <MDButton
        size="small"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        color={editor.isActive("blockquote") ? "dark" : "white"}
      >
        <Icon>format_quote</Icon>
      </MDButton>
    </MDBox>
  );
};

MenuBar.propTypes = {
  editor: PropTypes.object,
};

const SectionEditor = ({ section, index, onUpdate, onRemove, images }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
        orderedList: false,
      }),
      CustomOrderedList.configure({
        keepAttributes: true,
        HTMLAttributes: {
          class: 'ordered-list',
        },
      }),
      Color,
      TextStyle,
      FontSize,
      Image,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: `Escribe el contenido de la sección...` }),
    ],
    content: section.text || "",
    onUpdate: ({ editor }) => {
      onUpdate(index, { ...section, text: editor.getJSON() });
    },
  });

  // Sync content when it's loaded from backend
  useEffect(() => {
    if (editor && section.text && editor.isEmpty) {
      editor.commands.setContent(section.text);
    }
  }, [editor, section.text]);

  return (
    <MDBox mb={4} p={3} border={`1px solid ${darkMode ? "#444" : "#eee"}`} borderRadius="lg" bgColor={darkMode ? "transparent" : "#fafafa"}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MDTypography variant="h6" color="info">Sección {index + 1}</MDTypography>
        {index > 0 && (
          <MDButton variant="text" color="error" onClick={() => onRemove(index)} iconOnly>
            <Icon>delete</Icon>
          </MDButton>
        )}
      </MDBox>

      <MenuBar editor={editor} />

      <MDBox
        className="tiptap-wrapper"
        sx={{
          minHeight: "250px",
          border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
          borderRadius: "8px",
          padding: "16px",
          backgroundColor: "#fff",
          "& .ProseMirror": {
            color: "#000",
          }
        }}
      >
        <EditorContent editor={editor} />
      </MDBox>

      <MDBox mt={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Imagen después de esta sección</InputLabel>
              <Select
                value={section.imageIndex === null ? "" : section.imageIndex}
                label="Imagen después de esta sección"
                onChange={(e) => onUpdate(index, { ...section, imageIndex: e.target.value === "" ? null : e.target.value })}
                sx={{ height: 40 }}
              >
                <MenuItem value="">Ninguna</MenuItem>
                {images.map((img, i) => (
                  <MenuItem key={i} value={i}>
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <MDBox
                        component="img"
                        src={getImageUrl(img.file || img.url || img)}
                        width="30px"
                        height="30px"
                        sx={{ objectFit: 'cover', borderRadius: '4px' }}
                      />
                      <MDTypography variant="caption">
                        {img.section || `Imagen ${i + 1}`}
                      </MDTypography>
                    </MDBox>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <MDBox display="flex" flexDirection="column" gap={1}>
              <MDTypography variant="caption" color="text">
                Esta imagen aparecerá antes del texto de esta sección.  
              </MDTypography>
              {section.imageIndex !== null && section.imageIndex !== "" && images[section.imageIndex] && (
                <MDBox
                  component="img"
                  src={getImageUrl(images[section.imageIndex].file || images[section.imageIndex].url || images[section.imageIndex])}
                  alt={`Preview`}
                  sx={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ddd' }}
                />
              )}
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    </MDBox>
  );
};

SectionEditor.propTypes = {
  section: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  images: PropTypes.array.isRequired,
};

function ArticleEditor({ formData, setFormData, errors, setErrors, editor, loading, handleSave }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { id } = useParams();
  const [sections, setSections] = useState([]);
  const [institutions, setInstitutions] = useState([]);



  useEffect(() => {
    const fetchSections = async () => {
      try {
        const response = await sectionService.getAll();
        setSections(response.data);
      } catch (error) {
        console.error("Error fetching sections:", error);
      }
    };

    const fetchInstitution = async () => {
      try {
        const response = await institutionService.getAll();
        setInstitutions(response.data);
      } catch (error) {
        console.error("Error fetching sections:", error);
      }
    };
    fetchSections();
    fetchInstitution()
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (setErrors) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleImageUpload = (e) => {
    if (formData.images?.length >= 4) {
      alert("Puedes subir un máximo de 4 imágenes.");
      return;
    }
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => {
          const currentImages = prev.images || [];
          return {
            ...prev,
            images: [
              ...currentImages,
              {
                file: reader.result,
                isCover: currentImages.length === 0, // First one is cover by default
                caption: ""
              }
            ],
          };
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMarkAsCover = (index) => {
    setFormData((prev) => {
      const updatedImages = (prev.images || []).map((img, i) => ({
        ...img,
        isCover: i === index,
      }));
      return { ...prev, images: updatedImages };
    });
  };

  const handleImageCaptionChange = (index, caption) => {
    setFormData((prev) => {
      const updatedImages = [...(prev.images || [])];
      updatedImages[index] = { ...updatedImages[index], caption };
      return { ...prev, images: updatedImages };
    });
  };

  const removeImage = (index) => {
    setFormData((prev) => {
      const updatedImages = [...(prev.images || [])];
      const wasCover = updatedImages[index].isCover;
      updatedImages.splice(index, 1);

      // If we removed the cover, make the first remaining one the cover
      if (wasCover && updatedImages.length > 0) {
        updatedImages[0].isCover = true;
      }

      return { ...prev, images: updatedImages };
    });
  };

  const handleAddSource = () => {
    setFormData((prev) => ({ ...prev, sources: [...(prev.sources || []), ""] }));
  };

  const handleSourceChange = (index, value) => {
    setFormData((prev) => {
      const updatedSources = [...(prev.sources || [])];
      updatedSources[index] = value;
      return { ...prev, sources: updatedSources };
    });
  };

  const handleRemoveSource = (index) => {
    setFormData((prev) => {
      const updatedSources = [...(prev.sources || [])];
      updatedSources.splice(index, 1);
      return { ...prev, sources: updatedSources };
    });
  };

  return (
    <MDBox pt={0} pb={3} width="100%">
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h5">Contenido del Artículo</MDTypography>
              <MDBox mt={2}>
                <MDInput
                  label="Título"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors?.title}
                  required
                  inputProps={{ maxLength: 150 }}
                  helperText={`${formData.title?.length || 0}/150 caracteres`}
                />
                {errors?.title && (
                  <MDTypography variant="caption" color="error" fontWeight="light">
                    {errors.title}
                  </MDTypography>
                )}
              </MDBox>
              <MDBox mt={2}>
                <MDInput
                  label="Entradilla"
                  name="subtitle"
                  value={formData.subtitle}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={2}
                  error={!!errors?.subtitle}
                  required
                  inputProps={{ maxLength: 500 }}
                  helperText={`${formData.subtitle?.length || 0}/500 caracteres`}
                />
                {errors?.subtitle && (
                  <MDTypography variant="caption" color="error" fontWeight="light">
                    {errors.subtitle}
                  </MDTypography>
                )}
              </MDBox>
              <MDBox mt={3} mb={2}>
                {(formData.content || []).map((section, index) => (
                  <SectionEditor
                    key={index}
                    index={index}
                    section={section}
                    images={formData.images || []}
                    onUpdate={(idx, updatedSection) => {
                      const newContent = [...formData.content];
                      newContent[idx] = updatedSection;
                      setFormData({ ...formData, content: newContent });
                    }}
                    onRemove={(idx) => {
                      const newContent = [...formData.content];
                      newContent.splice(idx, 1);
                      setFormData({ ...formData, content: newContent });
                    }}
                  />
                ))}

                <MDButton
                  variant="outlined"
                  color="info"
                  fullWidth
                  onClick={() => {
                    setFormData({
                      ...formData,
                      content: [...formData.content, { text: {}, imageIndex: null }]
                    });
                  }}
                  sx={{ mt: 2, borderStyle: 'dashed' }}
                >
                  <Icon sx={{ mr: 1 }}>add</Icon> Agregar Nueva Sección de Texto
                </MDButton>

                {errors?.content && (
                  <MDTypography variant="caption" color="error" fontWeight="light" mt={2} display="block">
                    {errors.content}
                  </MDTypography>
                )}
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6">Configuración</MDTypography>
              <MDBox mt={2}>
                <FormControl fullWidth error={!!errors?.section}>
                  <InputLabel>Sección</InputLabel>
                  <Select
                    value={formData.section}
                    label="Sección"
                    name="section"
                    onChange={handleChange}
                    sx={{ height: 45 }}
                  >
                    {Array.isArray(sections) && sections.map((section) => (
                      <MenuItem key={section._id} value={section._id}>
                        {section.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {errors?.section && (
                  <MDTypography variant="caption" color="error" fontWeight="light">
                    {errors.section}
                  </MDTypography>
                )}
              </MDBox>
              <MDBox mt={2}>
                <MDInput
                  label="Etiquetas (separadas por coma)"
                  name="tags"
                  value={formData.tags}
                  onChange={handleChange}
                  fullWidth
                />
              </MDBox>
              <MDBox mt={2}>
                <FormControl fullWidth>
                  <InputLabel>Institución</InputLabel>
                  <Select
                    value={formData.institution}
                    label="Institución que avala"
                    name="institution"
                    onChange={handleChange}
                    sx={{ height: 45 }}
                  >
                    {Array.isArray(institutions) && institutions.map((institution) => (
                      <MenuItem key={institution._id} value={institution._id}>
                        {institution.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </MDBox>
              <Divider />
              <MDBox mt={2}>
                <MDTypography variant="button" fontWeight="medium">
                  Fuentes (Opcional)
                </MDTypography>
                <MDBox mt={1} pb={1} display="flex" flexDirection="column" gap={2}>
                  {(formData.sources || []).map((source, index) => (
                    <MDBox key={index} display="flex" gap={1} alignItems="center">
                      <MDInput
                        label={`Fuente ${index + 1}`}
                        value={source}
                        onChange={(e) => handleSourceChange(index, e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="Ej. https://ejemplo.com o Título del libro"
                      />
                      <MDButton variant="text" color="error" onClick={() => handleRemoveSource(index)} iconOnly size="small">
                        <Icon>delete</Icon>
                      </MDButton>
                    </MDBox>
                  ))}
                  <MDButton variant="outlined" color="dark" size="small" onClick={handleAddSource} sx={{ borderStyle: 'dashed' }}>
                    <Icon sx={{ mr: 1 }}>add</Icon> Añadir Fuente
                  </MDButton>
                </MDBox>
              </MDBox>
              <Divider />
              <MDBox mt={2}>
                <MDTypography variant="button" fontWeight="medium">
                  Gestión de Imágenes (Máx 4)
                </MDTypography>
                <MDBox mt={1} mb={2} display="flex" flexDirection="column" gap={2}>
                  {(formData.images || []).map((img, index) => (
                    <MDBox key={index} display="flex" flexDirection="column" gap={1} p={2} border={`1px solid ${darkMode ? "#444" : "#eee"}`} borderRadius="8px" bgColor={darkMode ? "transparent" : "#fafafa"}>
                      <MDBox display="flex" alignItems="center" gap={2}>
                        <MDBox
                          component="img"
                          src={getImageUrl(img.file || img.url || img)}
                          alt={`Img ${index + 1}`}
                          width="60px"
                          height="60px"
                          sx={{ objectFit: "cover", borderRadius: "8px", border: `1px solid ${darkMode ? "#444" : "#ddd"}` }}
                        />
                        <MDBox display="flex" flexDirection="column" flexGrow={1}>
                          <MDTypography
                            variant="caption"
                            color={img.isCover ? "info" : "text"}
                            fontWeight={img.isCover ? "bold" : "regular"}
                            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
                            onClick={() => handleMarkAsCover(index)}
                          >
                            <Icon fontSize="small">{img.isCover ? "check_circle" : "radio_button_unchecked"}</Icon>
                            {img.isCover ? "Imagen de Portada" : "Marcar como Portada"}
                          </MDTypography>
                        </MDBox>
                        <MDButton variant="text" color="error" onClick={() => removeImage(index)} iconOnly size="small">
                          <Icon>delete</Icon>
                        </MDButton>
                      </MDBox>
                      <MDInput
                        label="Pie de foto"
                        size="small"
                        value={img.caption || ""}
                        onChange={(e) => handleImageCaptionChange(index, e.target.value)}
                        fullWidth
                        placeholder="Escribe un pie de foto..."
                      />
                    </MDBox>
                  ))}
                  {(formData.images || []).length < 4 && (
                    <MDButton variant="outlined" color="info" component="label" fullWidth sx={{ borderStyle: 'dashed' }}>
                      <Icon sx={{ mr: 1 }}>upload</Icon> Subir Imagen
                      <input hidden accept="image/*" type="file" onChange={handleImageUpload} />
                    </MDButton>
                  )}
                </MDBox>
              </MDBox>
              <Divider />
              <MDBox mt={2}>
                <MDTypography variant="button" fontWeight="medium">
                  SEO
                </MDTypography>
                <MDBox mt={1}>
                  <MDInput
                    label="Meta Título"
                    name="metaTitle"
                    value={formData.metaTitle}
                    onChange={handleChange}
                    fullWidth
                  />
                </MDBox>
                <MDBox mt={2}>
                  <MDInput
                    label="Meta Descripción"
                    name="metaDescription"
                    value={formData.metaDescription}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={3}
                  />
                </MDBox>
              </MDBox>
              <MDBox mt={4}>
                <MDButton variant="gradient" color="info" fullWidth onClick={handleSave} disabled={loading}>
                  {loading ? "Guardando..." : id ? "Actualizar Artículo" : "Guardar"}
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}

ArticleEditor.propTypes = {
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  handleSave: PropTypes.func.isRequired,
};

export default ArticleEditor;
